import { useIsFocused } from "@react-navigation/native";
import { useContext, useEffect, useState, useCallback } from "react";
import {
  FlatList,
  Modal,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import PageTitle from "../../components/PageTitle";
import Button from "../../components/Button";
import { GlobalContext } from "../../contexts/global";
import { getAuditLogs } from "../../services/Api/admin";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";

const PAGE_SIZE = 20;

const ACTION_TYPES = [
  { label: "Todos", value: null },
  { label: "Ativar Usuário", value: "USER_ACTIVATE" },
  { label: "Desativar Usuário", value: "USER_DEACTIVATE" },
  { label: "Excluir Usuário", value: "USER_DELETE" },
  { label: "Crédito Carteira", value: "WALLET_CREDIT" },
  { label: "Débito Carteira", value: "WALLET_DEBIT" },
  { label: "Documento Aprovado", value: "DOCUMENT_APPROVE" },
  { label: "Documento Rejeitado", value: "DOCUMENT_REJECT" },
  { label: "Revisão de Documento", value: "DOCUMENT_REVIEW" },
];

export default function AdminAuditLog({ navigation }) {
  const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Filters
  const [actionType, setActionType] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [showActionTypePicker, setShowActionTypePicker] = useState(false);

  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const isFocused = useIsFocused();

  useEffect(() => {
    setLoading({
      show: true,
      message: "Carregando logs de auditoria",
    });
  }, []);

  useEffect(() => {
    if (isFocused) {
      loadLogs(1, true);
    }
  }, [isFocused]);


  // Reload when filters change
  useEffect(() => {
    loadLogs(1, true);
  }, [actionType, fromDate, toDate]);

  const loadLogs = useCallback(
    (pageNum = 1, reset = false) => {
      if (loadingMore && !reset) return;

      if (reset) {
        setLoadingMore(false);
      } else {
        setLoadingMore(true);
      }

      getAuditLogs(null, actionType, fromDate, toDate, pageNum, PAGE_SIZE)
        .then((response) => {
          const newLogs = response.items || [];
          if (reset) {
            setLogs(newLogs);
          } else {
            setLogs((prev) => [...prev, ...newLogs]);
          }
          setPage(pageNum);
          setHasMore(pageNum < response.totalPages);
        })
        .catch((error) => {
          const message =
            error?.message || error?.erro || "Erro ao carregar logs de auditoria";
          setShowAlert({
            type: "alert",
            title: "Erro",
            message: message,
            show: true,
            confirmText: "OK",
            pressConfirm: () => {
              setShowAlert({ ...showAlert, show: false });
            },
          });
        })
        .finally(() => {
          setLoading({ show: false, message: "" });
          setRefreshing(false);
          setLoadingMore(false);
        });
    },
    [actionType, fromDate, toDate, loadingMore, showAlert]
  );

  const handleRefresh = () => {
    setRefreshing(true);
    loadLogs(1, true);
  };

  const handleLoadMore = () => {
    if (hasMore && !loadingMore) {
      loadLogs(page + 1, false);
    }
  };

  const handleLogPress = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionTypeLabel = (type) => {
    const found = ACTION_TYPES.find((at) => at.value === type);
    return found ? found.label : type || "Desconhecido";
  };

  const getActionTypeIcon = (type) => {
    switch (type) {
      case "USER_ACTIVATE":
        return "account-check";
      case "USER_DEACTIVATE":
        return "account-off";
      case "USER_DELETE":
        return "account-remove";
      case "WALLET_CREDIT":
        return "wallet-plus";
      case "WALLET_DEBIT":
        return "cash-minus";
      case "DOCUMENT_APPROVE":
        return "clipboard-check";
      case "DOCUMENT_REJECT":
        return "clipboard-remove";
      case "DOCUMENT_REVIEW":
        return "clipboard-text";
      default:
        return "clipboard-text";
    }
  };

  const getActionTypeColor = (type) => {
    switch (type) {
      case "USER_ACTIVATE":
        return Colors.SUCCESS;
      case "USER_DEACTIVATE":
        return Colors.WARNING;
      case "USER_DELETE":
        return Colors.ALERT;
      case "WALLET_CREDIT":
        return Colors.SUCCESS;
      case "WALLET_DEBIT":
        return Colors.ALERT;
      case "DOCUMENT_APPROVE":
        return Colors.SUCCESS;
      case "DOCUMENT_REJECT":
        return Colors.ALERT;
      case "DOCUMENT_REVIEW":
        return Colors.INFO;
      default:
        return Colors.INFO;
    }
  };

  const clearFilters = () => {
    setActionType(null);
    setFromDate(null);
    setToDate(null);
  };

  const hasActiveFilters = actionType || fromDate || toDate;


  const setDateFilter = (daysAgo) => {
    const now = new Date();
    const from = new Date();
    from.setDate(now.getDate() - daysAgo);
    from.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);
    setFromDate(from);
    setToDate(now);
  };

  const renderLogItem = ({ item }) => (
    <TouchableOpacity
      style={styles.logItem}
      onPress={() => handleLogPress(item)}
      activeOpacity={0.7}
    >
      <View style={styles.logIconContainer}>
        <Icon
          name={getActionTypeIcon(item.actionType)}
          size={24}
          color={getActionTypeColor(item.actionType)}
        />
      </View>
      <View style={styles.logInfo}>
        <View style={styles.logHeader}>
          <Text style={styles.logActionType} numberOfLines={1}>
            {getActionTypeLabel(item.actionType)}
          </Text>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: item.success ? Colors.SUCCESS : Colors.ALERT },
            ]}
          >
            <Text style={styles.statusText}>
              {item.success ? "OK" : "Erro"}
            </Text>
          </View>
        </View>
        <Text style={styles.logAdmin} numberOfLines={1}>
          {item.adminEmail || "Admin desconhecido"}
        </Text>
        <Text style={styles.logTarget} numberOfLines={1}>
          {item.targetEntityType}: {item.targetEntityId}
        </Text>
        <Text style={styles.logTimestamp}>
          {formatDateTime(item.timestamp)}
        </Text>
      </View>
      <Icon name="chevron-right" size={24} color={Colors.GRAY_DARK} />
    </TouchableOpacity>
  );

  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <Icon name="clipboard-text-off" size={128} color={Colors.BLACK} />
      <Text style={styles.emptyText}>
        {hasActiveFilters
          ? "Nenhum log encontrado para os filtros selecionados."
          : "Nenhum log de auditoria registrado."}
      </Text>
    </View>
  );

  const renderFooter = () => {
    if (loadingMore) {
      return (
        <View style={styles.footerLoader}>
          <Text style={styles.loadingText}>Carregando mais...</Text>
        </View>
      );
    }
    
    if (hasMore && logs.length > 0) {
      return (
        <TouchableOpacity style={styles.loadMoreButton} onPress={handleLoadMore}>
          <Text style={styles.loadMoreText}>Carregar mais</Text>
        </TouchableOpacity>
      );
    }
    
    return null;
  };

  const renderFiltersSection = () => (
    <View style={styles.filtersContainer}>
      <TouchableOpacity
        style={styles.filterToggle}
        onPress={() => setShowFilters(!showFilters)}
      >
        <Icon
          name={showFilters ? "filter-off" : "filter"}
          size={20}
          color={Colors.PRIMARY}
        />
        <Text style={styles.filterToggleText}>
          {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
        </Text>
        {hasActiveFilters && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>!</Text>
          </View>
        )}
      </TouchableOpacity>

      {showFilters && (
        <View style={styles.filtersContent}>
          {/* Action Type Filter */}
          <Text style={styles.filterLabel}>Tipo de Ação</Text>
          <TouchableOpacity
            style={styles.filterSelect}
            onPress={() => setShowActionTypePicker(true)}
          >
            <Text style={styles.filterSelectText}>
              {getActionTypeLabel(actionType)}
            </Text>
            <Icon name="chevron-down" size={20} color={Colors.GRAY_DARK} />
          </TouchableOpacity>

          {/* Date Range Filter */}
          <Text style={styles.filterLabel}>Período</Text>
          <View style={styles.dateRangeButtons}>
            <TouchableOpacity
              style={[
                styles.dateRangeButton,
                !fromDate && !toDate && styles.dateRangeButtonActive,
              ]}
              onPress={() => {
                setFromDate(null);
                setToDate(null);
              }}
            >
              <Text
                style={[
                  styles.dateRangeButtonText,
                  !fromDate && !toDate && styles.dateRangeButtonTextActive,
                ]}
              >
                Todos
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setDateFilter(0)}
            >
              <Text style={styles.dateRangeButtonText}>Hoje</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setDateFilter(7)}
            >
              <Text style={styles.dateRangeButtonText}>7 dias</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setDateFilter(30)}
            >
              <Text style={styles.dateRangeButtonText}>30 dias</Text>
            </TouchableOpacity>
          </View>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={clearFilters}
            >
              <Icon name="close-circle" size={16} color={Colors.ALERT} />
              <Text style={styles.clearFiltersText}>Limpar Filtros</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );


  const renderActionTypePicker = () => (
    <Modal
      visible={showActionTypePicker}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowActionTypePicker(false)}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowActionTypePicker(false)}
      >
        <View style={styles.pickerModal}>
          <Text style={styles.pickerTitle}>Selecionar Tipo de Ação</Text>
          {ACTION_TYPES.map((type) => (
            <TouchableOpacity
              key={type.value ?? "all"}
              style={[
                styles.pickerOption,
                actionType === type.value && styles.pickerOptionSelected,
              ]}
              onPress={() => {
                setActionType(type.value);
                setShowActionTypePicker(false);
              }}
            >
              <Text
                style={[
                  styles.pickerOptionText,
                  actionType === type.value && styles.pickerOptionTextSelected,
                ]}
              >
                {type.label}
              </Text>
              {actionType === type.value && (
                <Icon name="check" size={20} color={Colors.PRIMARY} />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  const renderDetailModal = () => {
    if (!selectedLog) return null;

    let details = {};
    try {
      details = selectedLog.details ? JSON.parse(selectedLog.details) : {};
    } catch (e) {
      details = { raw: selectedLog.details };
    }

    return (
      <Modal
        visible={showDetailModal}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowDetailModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.detailModal}>
            <View style={styles.detailHeader}>
              <Text style={styles.detailTitle}>Detalhes do Log</Text>
              <TouchableOpacity
                onPress={() => setShowDetailModal(false)}
                style={styles.closeButton}
              >
                <Icon name="close" size={24} color={Colors.BLACK} />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.detailContent}>
              {/* Action Info */}
              <View style={styles.detailSection}>
                <View style={styles.detailRow}>
                  <Icon
                    name={getActionTypeIcon(selectedLog.actionType)}
                    size={32}
                    color={getActionTypeColor(selectedLog.actionType)}
                  />
                  <View style={styles.detailRowContent}>
                    <Text style={styles.detailLabel}>Tipo de Ação</Text>
                    <Text style={styles.detailValue}>
                      {getActionTypeLabel(selectedLog.actionType)}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Status */}
              <View style={styles.detailSection}>
                <View style={styles.detailRow}>
                  <Icon
                    name={selectedLog.success ? "check-circle" : "alert-circle"}
                    size={32}
                    color={selectedLog.success ? Colors.SUCCESS : Colors.ALERT}
                  />
                  <View style={styles.detailRowContent}>
                    <Text style={styles.detailLabel}>Status</Text>
                    <Text
                      style={[
                        styles.detailValue,
                        {
                          color: selectedLog.success
                            ? Colors.SUCCESS
                            : Colors.ALERT,
                        },
                      ]}
                    >
                      {selectedLog.success ? "Sucesso" : "Falha"}
                    </Text>
                  </View>
                </View>
              </View>

              {/* Error Message (if failed) */}
              {!selectedLog.success && selectedLog.errorMessage && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Mensagem de Erro</Text>
                  <Text style={styles.errorMessage}>
                    {selectedLog.errorMessage}
                  </Text>
                </View>
              )}

              {/* Admin Info */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Administrador</Text>
                <View style={styles.detailInfoRow}>
                  <Text style={styles.detailInfoLabel}>Email:</Text>
                  <Text style={styles.detailInfoValue}>
                    {selectedLog.adminEmail || "N/A"}
                  </Text>
                </View>
                <View style={styles.detailInfoRow}>
                  <Text style={styles.detailInfoLabel}>ID:</Text>
                  <Text style={styles.detailInfoValue}>
                    {selectedLog.adminUserId || "N/A"}
                  </Text>
                </View>
              </View>

              {/* Target Info */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Alvo da Ação</Text>
                <View style={styles.detailInfoRow}>
                  <Text style={styles.detailInfoLabel}>Tipo:</Text>
                  <Text style={styles.detailInfoValue}>
                    {selectedLog.targetEntityType || "N/A"}
                  </Text>
                </View>
                <View style={styles.detailInfoRow}>
                  <Text style={styles.detailInfoLabel}>ID:</Text>
                  <Text style={styles.detailInfoValue}>
                    {selectedLog.targetEntityId || "N/A"}
                  </Text>
                </View>
              </View>

              {/* Timestamp and IP */}
              <View style={styles.detailSection}>
                <Text style={styles.detailSectionTitle}>Informações Adicionais</Text>
                <View style={styles.detailInfoRow}>
                  <Text style={styles.detailInfoLabel}>Data/Hora:</Text>
                  <Text style={styles.detailInfoValue}>
                    {formatDateTime(selectedLog.timestamp)}
                  </Text>
                </View>
                <View style={styles.detailInfoRow}>
                  <Text style={styles.detailInfoLabel}>IP:</Text>
                  <Text style={styles.detailInfoValue}>
                    {selectedLog.ipAddress || "N/A"}
                  </Text>
                </View>
              </View>

              {/* Details JSON */}
              {Object.keys(details).length > 0 && (
                <View style={styles.detailSection}>
                  <Text style={styles.detailSectionTitle}>Detalhes</Text>
                  {Object.entries(details).map(([key, value]) => (
                    <View key={key} style={styles.detailInfoRow}>
                      <Text style={styles.detailInfoLabel}>{key}:</Text>
                      <Text style={styles.detailInfoValue}>
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
            </ScrollView>

            <Button
              textoBotao="Fechar"
              estilo={styles.closeModalButton}
              onPress={() => setShowDetailModal(false)}
            />
          </View>
        </View>
      </Modal>
    );
  };


  return (
    <PageTitle title={"Logs de Auditoria"} navigation={navigation}>
      <View style={styles.container}>
        {renderFiltersSection()}
        <FlatList
          data={logs}
          renderItem={renderLogItem}
          keyExtractor={(item) => item.id.toString()}
          contentContainerStyle={logs.length === 0 ? styles.emptyList : null}
          ListEmptyComponent={renderEmptyList}
          ListFooterComponent={renderFooter}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[Colors.PRIMARY]}
              tintColor={Colors.PRIMARY}
            />
          }
          ItemSeparatorComponent={() => <View style={styles.separator} />}
        />
        {renderActionTypePicker()}
        {renderDetailModal()}
      </View>
    </PageTitle>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.WHITE,
  },
  filtersContainer: {
    backgroundColor: Colors.SNOW,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  filterToggle: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
  },
  filterToggleText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    marginLeft: 8,
    fontWeight: "bold",
  },
  filterBadge: {
    backgroundColor: Colors.ALERT,
    borderRadius: 10,
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
  },
  filterBadgeText: {
    color: Colors.WHITE,
    fontSize: Fonts.FONT_SIZE_12,
    fontWeight: "bold",
  },
  filtersContent: {
    padding: 12,
    paddingTop: 0,
  },
  filterLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 8,
    marginTop: 8,
  },
  filterSelect: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: Colors.WHITE,
    borderWidth: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderRadius: 8,
    padding: 12,
  },
  filterSelectText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
  },
  dateRangeButtons: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  dateRangeButton: {
    backgroundColor: Colors.WHITE,
    borderWidth: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  dateRangeButtonActive: {
    backgroundColor: Colors.PRIMARY,
    borderColor: Colors.PRIMARY,
  },
  dateRangeButtonText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.BLACK,
  },
  dateRangeButtonTextActive: {
    color: Colors.WHITE,
  },
  clearFiltersButton: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
  },
  clearFiltersText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.ALERT,
    marginLeft: 4,
  },
  logItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: Colors.WHITE,
  },
  logIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.SNOW,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  logInfo: {
    flex: 1,
  },
  logHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  logActionType: {
    fontSize: Fonts.FONT_SIZE_14,
    fontWeight: "bold",
    color: Colors.BLACK,
    flex: 1,
    marginRight: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  statusText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  logAdmin: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.PRIMARY,
    marginBottom: 2,
  },
  logTarget: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
    marginBottom: 2,
  },
  logTimestamp: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
  },
  separator: {
    height: 1,
    backgroundColor: Colors.GRAY_LIGHT,
    marginHorizontal: 16,
  },
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    paddingVertical: 48,
  },
  emptyText: {
    marginTop: 24,
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    textAlign: "center",
  },
  emptyList: {
    flexGrow: 1,
  },
  footerLoader: {
    padding: 16,
    alignItems: "center",
  },
  loadingText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  loadMoreButton: {
    padding: 16,
    alignItems: "center",
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.PRIMARY,
  },
  loadMoreText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },

  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  pickerModal: {
    backgroundColor: Colors.WHITE,
    borderRadius: 12,
    padding: 16,
    width: "80%",
    maxHeight: "60%",
  },
  pickerTitle: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 16,
    textAlign: "center",
  },
  pickerOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  pickerOptionSelected: {
    backgroundColor: Colors.SNOW,
  },
  pickerOptionText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
  },
  pickerOptionTextSelected: {
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },
  detailModal: {
    backgroundColor: Colors.WHITE,
    borderRadius: 12,
    width: "90%",
    maxHeight: "85%",
  },
  detailHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  detailTitle: {
    fontSize: Fonts.FONT_SIZE_18,
    fontWeight: "bold",
    color: Colors.BLACK,
  },
  closeButton: {
    padding: 4,
  },
  detailContent: {
    padding: 16,
  },
  detailSection: {
    marginBottom: 16,
  },
  detailRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  detailRowContent: {
    marginLeft: 12,
  },
  detailLabel: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
  },
  detailValue: {
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    fontWeight: "bold",
  },
  detailSectionTitle: {
    fontSize: Fonts.FONT_SIZE_14,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 8,
  },
  detailInfoRow: {
    flexDirection: "row",
    marginBottom: 4,
  },
  detailInfoLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    width: 180,
  },
  detailInfoValue: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
    flex: 1,
  },
  errorMessage: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.ALERT,
    backgroundColor: Colors.SNOW,
    padding: 8,
    borderRadius: 4,
  },
  closeModalButton: {
    backgroundColor: Colors.PRIMARY,
    margin: 16,
    marginTop: 0,
  },
});
